import { useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconZoomOutline } from '@iwoca/icons';
import { Button, Input } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { TApplicationStage, TSpendingLimitStatus } from '../../Customers.types';
import { CustomersContentSwitcher } from '../CustomersContentSwitcher/CustomersContentSwitcher';
import { CustomersFilterDrawer } from '../CustomersFilterDrawer/CustomersFilterDrawer';
import { ExportCustomers } from '../ExportCustomers/ExportCustomers';

export const CustomersToolbar = ({
  setCurrentPage,
  setSearchQuery,
}: {
  setCurrentPage: (page: number) => void;
  setSearchQuery: (query: string) => void;
}) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement>(null);

  const methods = useForm({
    defaultValues: {
      status: searchParams.get('status'),
      stage: searchParams.get('stage'),
    },
  });

  const updateQueryParams = ({
    status,
    stage,
  }: {
    status: TSpendingLimitStatus['value'] | null;
    stage: TApplicationStage['value'] | null;
  }) => {
    setSearchParams((params) => {
      if (status) {
        params.set('status', status);
      } else {
        params.delete('status');
      }
      if (stage) {
        params.set('stage', stage);
      } else {
        params.delete('stage');
      }
      return params;
    });

    setCurrentPage(1);
  };

  const exportFilters = {
    status: searchParams.get('status') || null,
    stage: searchParams.get('stage') || null,
    searchQuery: searchRef.current?.value || '',
  };

  return (
    <div className="flex flex-col">
      <div className="mb-s flex justify-end">
        <ExportCustomers filters={exportFilters} />
      </div>
      <div>
        <div className="grid auto-rows-max grid-cols-[2fr_1fr] m:grid-cols-[1fr] m:gap-l">
          <FormProvider {...methods}>
            <div className="flex gap-l xs:flex-col xs:gap-0 m:justify-between">
              <CustomersFilterDrawer
                isOpen={isFilterDrawerOpen}
                setIsOpen={setIsFilterDrawerOpen}
                updateQueryParams={updateQueryParams}
              />
              <div className="m:w-full">
                <CustomersContentSwitcher
                  updateQueryParams={updateQueryParams}
                />
              </div>
              <Button
                variant="tertiary"
                onClick={() => {
                  setIsFilterDrawerOpen(true);
                  tracking.ampli.customersPageMoreFiltersClicked();
                }}
                className="!text-s xs:self-end"
              >
                More filters
              </Button>
            </div>
          </FormProvider>
          <div className="w-full m:row-start-2">
            <Input
              placeholder="Search buyer..."
              iconLeft={<IconZoomOutline />}
              ref={searchRef}
              type="search"
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter')
                  setSearchQuery(searchRef.current!.value);
              }}
              className="[&>div]:flex"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
